import React, { useContext } from 'react';
import { Navigate, useParams } from 'react-router';
import { SessionContext } from '#client/contexts/SessionContext.jsx';
import { Spinner } from '@shopify/polaris';
import NoPermissions from '#client/pages/NoPermissions';
import axios from 'axios';
import { useQuery } from '@tanstack/react-query';
import { getCustomerProgramAdminQueryOptions } from '#client/utils/queryOptions.js';

const CustomerProtectedRoute = ({ children }) => {
    const { id: programId } = useParams();
    const customerId = localStorage.getItem('customerId');
    const sessionContext = useContext(SessionContext);

    // Extract isCustomerAuthenticated with a fallback
    const isCustomerAuthenticated = sessionContext?.isCustomerAuthenticated || false;

    // Fetch program admin status using TanStack Query
    const { data, isLoading, isError } = useQuery({
        ...getCustomerProgramAdminQueryOptions(customerId, programId),
        queryFn: async () => {
            const API_URL = import.meta.env.VITE_API_URL;
            const response = await axios.get(`${API_URL}/auth/customer/me`, {
                params: {
                    customerId,
                    programId,
                },
            });
            return response.data.data;
        },
        enabled: isCustomerAuthenticated && !!programId && !!customerId,
    });

    const isProgramAdmin = data?.isProgramAdmin || false;

    // Safety check: if context is not available, show error
    if (!sessionContext) {
        console.error('SessionContext is not available. Make sure CustomerProtectedRoute is used within SessionProvider.');
        return <div>Error: Session context not available</div>;
    }

    // Not authenticated at all
    if (!isCustomerAuthenticated) {
        return <Navigate to="/" replace />;
    }

    // If no programId or customerId, skip the check and allow access
    if (!programId || !customerId) {
        return children;
    }

    // Still loading the admin check
    if (isLoading) {
        return <Spinner accessibilityLabel="Checking permissions" size="large" />;
    }

    // If there was an error or not a program admin
    if (isError || !isProgramAdmin) {
        return <NoPermissions permission="program" />;
    }

    // Authenticated and authorized
    return children;
};

export default CustomerProtectedRoute;
